import { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../components/auth/authConfig";
import { useApi } from "./../hooks/useApi";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const { get } = useApi();
    const didStartLogin = useRef(false); // evita doble login en StrictMode

    const fetchPermissions = useCallback(async () => {
        try {
            const data = await get("getProfile");
            console.log(data);
            setUser((prev) => ({
                ...prev,
                permissions: data?.usuario && Array.isArray(data.usuario.permissions) ? data.usuario.permissions : [],
            }));
        } catch {
            setUser((prev) => ({ ...prev, permissions: [] }));
        }
    }, [get]);

    useEffect(() => {
        const initAuth = async () => {
            // 1) Si MSAL está en interacción, espera (evita interaction_in_progress)
            if (inProgress !== InteractionStatus.None) return;

            try {
                // 2) Determina cuenta activa
                const account = instance.getActiveAccount() ?? accounts[0];

                if (account) {
                    setUser({ username: account.username, name: account.name, permissions: [] });

                    const tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account });
                    setToken(tokenResponse.accessToken);
                    localStorage.setItem("token", tokenResponse.accessToken);

                    await fetchPermissions();
                    setLoading(false);
                    return;
                }

                // 3) Sin cuenta aún → intenta SSO silencioso (opcional)
                try {
                    const ssoResponse = await instance.ssoSilent(loginRequest);
                    const acc = ssoResponse.account;
                    instance.setActiveAccount(acc);

                    setUser({ username: acc.username, name: acc.name, permissions: [] });

                    const tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account: acc });
                    setToken(tokenResponse.accessToken);
                    localStorage.setItem("token", tokenResponse.accessToken);

                    await fetchPermissions();
                } catch (error) {
                    // 4) Si requiere interacción y no hay ninguna en curso → redirect
                    if (error instanceof InteractionRequiredAuthError && !didStartLogin.current) {
                        didStartLogin.current = true;
                        await instance.loginRedirect(loginRequest);
                        return;
                    } else {
                        console.error("Error en silent/SSO:", error);
                    }
                }
            } catch (err) {
                console.error("Error al inicializar Auth:", err);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, [instance, accounts, inProgress, fetchPermissions]);

    const login = async () => {
        try {
            // Evita popup si ya hay interacción
            if (inProgress !== InteractionStatus.None) return;

            const response = await instance.loginPopup(loginRequest);
            const account = response.account;
            instance.setActiveAccount(account);

            setUser({ username: account.username, name: account.name, permissions: [] });

            const tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account });
            setToken(tokenResponse.accessToken);
            localStorage.setItem("token", tokenResponse.accessToken);

            await fetchPermissions();
        } catch (error) {
            console.error("Error en login popup:", error);
        }
    };

    const logout = async () => {
        await instance.logoutRedirect();
        setUser(null);
        setToken(null);
        localStorage.removeItem("token");
    };

    if (loading) return <div>Cargando sesión...</div>;

    return (
        <AuthContext.Provider value={{ user, token, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}